/**
 * Invoice review flags — the vocabulary shared by the API, the typed clients
 * and the web app.
 *
 * A flag records that a generated invoice differs from the one before it in a
 * way a human should look at before approving. It is an observation, never a
 * task: there is no "clear" action and no assignee. The only way a flag leaves
 * a reviewer's queue is the invoice leaving `draft`.
 *
 * ## Why the reason is a closed set
 *
 * `invoice_generation_run_items.skip_reason` is free text, and that is right
 * for what it is — prose a human reads once on a run page. A flag reason is
 * the opposite: it is grouped, counted and reported on, and reporting over
 * free text means reporting over whatever strings happened to get written. So
 * the set below is closed, mirrored by a CHECK constraint on the column, and
 * adding to it costs a migration. That price is the point.
 *
 * ## Why the facts are structured
 *
 * Everything a human reads about a flag is DERIVED from {@link InvoiceFlagFacts}
 * by {@link describeInvoiceFlag}, which is the only place that turns a flag
 * into English. The list popover, the invoice panel and both exports therefore
 * cannot describe the same flag differently, and a report can aggregate
 * `facts->>'delta'` without parsing prose.
 */

import Big from 'big.js';
import type { CustomerType } from './customer-classification';
import type { InvoiceLineKind } from './invoices';
import { compareMoney, isMoney, MONEY_DP, toMoney } from './money';
import type { SowFrequency } from './sows';

/**
 * Why an invoice was flagged.
 *
 * Deliberately eight narrow reasons rather than one `TERMS_CHANGED` with a
 * detail string: a reviewer works all the cadence changes at once, and a
 * report groups by this column. One reason covering four situations can do
 * neither.
 */
export const INVOICE_FLAG_REASONS = [
  'AMOUNT_CHANGED',
  'SOW_AMENDED',
  'CADENCE_CHANGED',
  'CUSTOMER_TYPE_CHANGED',
  'FIRST_INVOICE',
  'ZERO_TOTAL',
  'NEGATIVE_TOTAL',
  'MISSING_EXPECTED_LINES',
  'MISSING_VARIABLE_EXPENSE',
  'UNPRICED_ROLE',
  'RETAINER_ALREADY_BILLED',
  'SUMMARY_UNAVAILABLE',
  'MANUAL_REVIEW',
  'ACCOUNTS_INCOMPLETE',
] as const;
export type InvoiceFlagReason = (typeof INVOICE_FLAG_REASONS)[number];

export const INVOICE_FLAG_REASON_LABELS: Record<InvoiceFlagReason, string> = {
  AMOUNT_CHANGED: 'Amount changed',
  SOW_AMENDED: 'SOW amended',
  CADENCE_CHANGED: 'Billing cadence changed',
  CUSTOMER_TYPE_CHANGED: 'Customer type changed',
  FIRST_INVOICE: 'First invoice',
  ZERO_TOTAL: 'Zero total',
  NEGATIVE_TOTAL: 'Negative total',
  MISSING_EXPECTED_LINES: 'Missing expected lines',
  MISSING_VARIABLE_EXPENSE: 'Missing variable expense',
  UNPRICED_ROLE: 'Role not priced by the SOW',
  RETAINER_ALREADY_BILLED: 'Retainer already billed',
  SUMMARY_UNAVAILABLE: 'Work description unavailable',
  MANUAL_REVIEW: 'Require manual review',
  ACCOUNTS_INCOMPLETE: 'Lines without an account',
};

/**
 * How hard a flag pushes.
 *
 * STORED on the row rather than derived in TypeScript, even though it is a
 * pure function of the reason: a SQL reader — a report, an export, an ad-hoc
 * query — has no access to the map below, and ranking severity is the first
 * thing any of them wants to do.
 */
export const INVOICE_FLAG_SEVERITIES = ['info', 'review', 'blocking'] as const;
export type InvoiceFlagSeverity = (typeof INVOICE_FLAG_SEVERITIES)[number];

export const INVOICE_FLAG_SEVERITY_LABELS: Record<InvoiceFlagSeverity, string> = {
  info: 'For information',
  review: 'Needs review',
  blocking: 'Needs a decision',
};

/**
 * The severity each reason carries.
 *
 * `FIRST_INVOICE` is `info` because it is not evidence of a mistake — it says
 * only that there was nothing to compare against, which is materially
 * different from "compared, and unchanged" and must not be silently collapsed
 * into it. The two totals that cannot be right — zero and negative — are
 * `blocking`; everything else is a difference someone should look at.
 */
export const INVOICE_FLAG_SEVERITY_BY_REASON: Record<InvoiceFlagReason, InvoiceFlagSeverity> = {
  AMOUNT_CHANGED: 'review',
  SOW_AMENDED: 'review',
  CADENCE_CHANGED: 'review',
  CUSTOMER_TYPE_CHANGED: 'review',
  FIRST_INVOICE: 'info',
  ZERO_TOTAL: 'blocking',
  NEGATIVE_TOTAL: 'blocking',
  MISSING_EXPECTED_LINES: 'review',
  // `review`, never `blocking`. The invoice is not wrong, it is incomplete,
  // and there is a legitimate reading where it is also correct: nothing was
  // incurred this month. Only the person who knows can say which — which is
  // what a review flag is for. Blocking would stop a whole run's approvals
  // over a fee that may not exist.
  MISSING_VARIABLE_EXPENSE: 'review',
  // `blocking`, unlike every other incompleteness here. The hours are ON the
  // document at 0.00, so approving without looking sends a client a line that
  // says we worked and charged nothing for it — a figure that is wrong rather
  // than merely absent, and one no later invoice will correct because the
  // period will already have been billed. A human has to supply the rate.
  UNPRICED_ROLE: 'blocking',
  // `review`. There is a legitimate reading — the retainer genuinely was
  // billed in advance and this invoice correctly carries only the hours — and
  // an illegitimate one where the same period is about to go out twice. Only
  // someone who can look at both documents can say which.
  RETAINER_ALREADY_BILLED: 'review',
  // `review`. The line's hours and money are right — what is missing is the
  // paragraph saying what the work WAS, which on a T&M invoice is most of why
  // the client accepts the number. Not blocking: a reviewer who knows the
  // engagement can type the description and approve, and holding the whole
  // invoice for a summarizer outage would be the tail wagging the dog.
  SUMMARY_UNAVAILABLE: 'review',
  // `review`. Nothing is wrong with the invoice — the engagement is marked
  // `manual` because its terms are ones a person must check every time.
  MANUAL_REVIEW: 'review',
  // Approval refuses the invoice until every line is booked, so this is a
  // gate in fact, not just in colour.
  ACCOUNTS_INCOMPLETE: 'blocking',
};

/** Rank for sorting. Higher is louder — an invoice's severity is the maximum
 *  over its flags, which is what the list's severity stripe shows. */
export const INVOICE_FLAG_SEVERITY_RANK: Record<InvoiceFlagSeverity, number> = {
  info: 0,
  review: 1,
  blocking: 2,
};

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

/** One field that differed between two SOW versions. Values are rendered
 *  strings, not raw column values: the diff is read, never recomputed. */
export interface SowFieldChange {
  field: string;
  label: string;
  from: string | null;
  to: string | null;
}

/** A line kind that billed last period and did not bill this one. */
export interface MissingLineKind {
  kind: InvoiceLineKind;
  priorAmount: string;
}

/**
 * What the evaluator observed, per reason.
 *
 * Discriminated on `reason` so a formatter that forgets a member fails to
 * compile rather than rendering an empty cell (G15). Money stays a string
 * throughout — see `money.ts` for why nothing here is a `number`.
 */
export type InvoiceFlagFacts =
  | {
      reason: 'AMOUNT_CHANGED';
      priorTotal: string;
      currentTotal: string;
      /** Signed: negative when the invoice went down. */
      delta: string;
      /** Signed, two decimal places. Null when the prior total was zero —
       *  the percentage is undefined, not 100. */
      deltaPct: string | null;
      currency: string;
    }
  | {
      reason: 'SOW_AMENDED';
      priorVersion: number;
      currentVersion: number;
      changed: SowFieldChange[];
    }
  | {
      /**
       * The SOW LINE's billing frequency, snapshotted by the run that raised
       * each invoice — `SowFrequency`, not the customer's `InvoiceCadence`.
       *
       * That is what the column actually holds: the run writes
       * `invoiceCadence: c.sowFrequency`, and `sows.ts` notes that the run's
       * `invoice_cadences` / `customer_types` columns kept their old names
       * while their CONTENTS moved to the SOW's vocabulary. The two sets are
       * close enough to confuse and are not the same (`semi_annual` vs
       * `half_yearly`), so typing this as the customer's cadence would drop
       * `semi_annual` and `annual` on the floor and report a change to
       * nothing.
       */
      reason: 'CADENCE_CHANGED';
      from: SowFrequency | null;
      to: SowFrequency | null;
    }
  | {
      reason: 'CUSTOMER_TYPE_CHANGED';
      from: CustomerType | null;
      to: CustomerType | null;
    }
  | {
      reason: 'FIRST_INVOICE';
      /** When the agreement started, so a reviewer can tell a genuinely new
       *  engagement from one that has simply never been invoiced. */
      sowEffectiveFrom: string | null;
    }
  | {
      reason: 'ZERO_TOTAL';
      currency: string;
      /** What it billed last period, when there was one. The number that makes
       *  a zero alarming rather than merely odd. */
      priorTotal: string | null;
    }
  | {
      reason: 'NEGATIVE_TOTAL';
      total: string;
      currency: string;
    }
  | {
      reason: 'MISSING_EXPECTED_LINES';
      missing: MissingLineKind[];
      currency: string;
    }
  | {
      /**
       * Hours were worked under a service role the SOW's T&M line does not
       * price. The line prints with its hours and a zero amount — see
       * `docs/invoice-generation.md` — deliberately NOT at the tenant's
       * global rate-card rate: that rate is a default the customer never
       * signed, and billing it silently is how an unagreed number reaches a
       * client.
       */
      reason: 'UNPRICED_ROLE';
      roles: UnpricedRole[];
    }
  | {
      /**
       * This SOW is hybrid, so the T&M run raises the whole invoice — but a
       * retainer invoice for the same period already exists, raised in
       * advance before the SOW became hybrid or before the arrears rule
       * applied. The retainer lines are therefore LEFT OFF this invoice and
       * the earlier document is named here, so a reviewer can confirm the
       * pairing instead of discovering the overlap from the customer.
       */
      reason: 'RETAINER_ALREADY_BILLED';
      priorInvoiceId: string;
      priorInvoiceNumber: string;
      priorTotal: string;
      currency: string;
    }
  | {
      /**
       * One or more roles' work descriptions could not be produced, so their
       * lines carry the role and period but no account of what was done. The
       * hours and the amount are unaffected.
       */
      reason: 'SUMMARY_UNAVAILABLE';
      roles: UndescribedRole[];
    }
  | {
      reason: 'MISSING_VARIABLE_EXPENSE';
      /**
       * The lines that were in force for the period with no amount recorded,
       * so the reviewer knows WHICH fee to go and find. Named by the line's
       * own description and its catalog item — the two things that identify it
       * on the customer's Invoicing tab and in the month grid.
       */
      lines: UnpricedVariableLine[];
    }
  | {
      /**
       * At least one engagement billed on this invoice is marked `manual`:
       * its invoices are always checked by a person before they go out.
       * Named by engagement so a grouped invoice says which one asked.
       */
      reason: 'MANUAL_REVIEW';
      engagements: string[];
    }
  | {
      /**
       * A generated invoice has lines whose money is not fully booked to the
       * chart of accounts: no account at all, or only part of the line. The
       * run leaves a line unassigned rather than guess — most often because
       * the account it would have used is inactive. Approval refuses the
       * invoice until a person books every line.
       */
      reason: 'ACCOUNTS_INCOMPLETE';
      lines: UnbookedLine[];
      /** Invoice-settings defaults that point at an inactive account — the
       *  usual reason a generated line came out unassigned. */
      inactiveDefaults: InactiveDefaultAccount[];
    };

/** One line whose money is not fully booked. Money stays a string (G15). */
export interface UnbookedLine {
  lineNo: number;
  lineLabel: string;
  amount: string;
  /** What its allocations add up to — `0.00` when it has none. */
  booked: string;
}

/** A default account that is inactive, and which line type it is the
 *  default for. */
export interface InactiveDefaultAccount {
  slot: 'retainer' | 'service' | 'expense' | 'discount';
  code: string;
  name: string;
}

/**
 * One T&M line whose role the SOW does not price.
 *
 * Named by the line's own printed heading rather than by a role looked up
 * elsewhere: the flag is recomputed from the invoice on every save, and the
 * heading is what a reviewer is actually looking at on the document. `hours`
 * is the line's quantity, a two-decimal string, never a number (G15).
 */
export interface UnpricedRole {
  /** The line's `source_id` — the service role — or null if it carries none. */
  serviceRoleId: string | null;
  /** The line's heading, e.g. `'Controller - June 2026'`. */
  lineLabel: string;
  hours: string;
}

/** One role whose description could not be generated, and why — the reason
 *  comes from the summarizer, which reports per group rather than failing the
 *  batch. */
export interface UndescribedRole {
  serviceRoleId: string | null;
  /** The line's heading, which is all the description it has. */
  lineLabel: string;
}

/** One variable expense line that was left off an invoice because nothing was
 *  recorded against it for the period. */
export interface UnpricedVariableLine {
  lineId: string;
  description: string;
  itemName: string;
}

/** A flag as the evaluator produces it, before it is a row. */
export interface InvoiceFlagInput {
  reason: InvoiceFlagReason;
  severity: InvoiceFlagSeverity;
  facts: InvoiceFlagFacts;
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/** Validate rather than blind-cast when reading a CHECK-constrained column
 *  back off a row, so a value that somehow got past the constraint surfaces
 *  as a fallback rather than an unsound type. */
export function isInvoiceFlagReason(v: unknown): v is InvoiceFlagReason {
  return typeof v === 'string' && (INVOICE_FLAG_REASONS as readonly string[]).includes(v);
}

export function isInvoiceFlagSeverity(v: unknown): v is InvoiceFlagSeverity {
  return typeof v === 'string' && (INVOICE_FLAG_SEVERITIES as readonly string[]).includes(v);
}

// ---------------------------------------------------------------------------
// Percentage
// ---------------------------------------------------------------------------

/**
 * Signed percentage change from `from` to `to`, or null when it is undefined.
 *
 * Null — not zero, and not 100 — when the base is zero: an invoice that went
 * from nothing to $5,000 has no meaningful percentage, and rendering one
 * invents a figure nobody can check. The caller shows the two amounts instead.
 *
 * Not in `money.ts` because a percentage is not money: it has its own scale
 * and its own division, which that module deliberately does not expose.
 */
export function percentChangeOfMoney(from: string, to: string): string | null {
  if (!isMoney(from) || !isMoney(to)) return null;
  const base = new Big(from);
  if (base.eq(0)) return null;
  return new Big(to).minus(base).div(base).times(100).toFixed(MONEY_DP);
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/** A flag rendered for a human. The ONE place facts become English. */
export interface InvoiceFlagDescription {
  title: string;
  /** One line of specifics. Empty string when the title says everything. */
  detail: string;
}

function money(amount: string, currency: string): string {
  // No `Intl` here: this module is shared with the API, which formats nothing
  // for a locale, and a currency-aware render belongs to the web app. The
  // exports want the plain figure anyway.
  return `${currency} ${toMoney(amount)}`;
}

function signed(amount: string): string {
  return compareMoney(amount, '0') >= 0 ? `+${toMoney(amount)}` : toMoney(amount);
}

/**
 * Describe a flag.
 *
 * Exhaustive over {@link InvoiceFlagFacts} — the `never` in the default branch
 * is what makes adding a reason without a description a compile error rather
 * than a blank cell in the export.
 */
export function describeInvoiceFlag(facts: InvoiceFlagFacts): InvoiceFlagDescription {
  const title = INVOICE_FLAG_REASON_LABELS[facts.reason];
  switch (facts.reason) {
    case 'AMOUNT_CHANGED': {
      const pct = facts.deltaPct === null ? '' : `, ${signed(facts.deltaPct)}%`;
      return {
        title,
        detail: `${money(facts.priorTotal, facts.currency)} → ${money(facts.currentTotal, facts.currency)} (${signed(facts.delta)}${pct})`,
      };
    }
    case 'SOW_AMENDED': {
      const fields =
        facts.changed.length === 0
          ? 'no comparable field differed'
          : facts.changed.map((c) => c.label).join(', ');
      return { title, detail: `v${facts.priorVersion} → v${facts.currentVersion} · ${fields}` };
    }
    case 'CADENCE_CHANGED':
      return { title, detail: `${facts.from ?? 'none'} → ${facts.to ?? 'none'}` };
    case 'CUSTOMER_TYPE_CHANGED':
      return { title, detail: `${facts.from ?? 'none'} → ${facts.to ?? 'none'}` };
    case 'FIRST_INVOICE':
      return {
        title,
        detail:
          facts.sowEffectiveFrom === null
            ? 'No previous invoice for this agreement'
            : `No previous invoice; agreement effective from ${facts.sowEffectiveFrom}`,
      };
    case 'ZERO_TOTAL':
      return {
        title,
        detail:
          facts.priorTotal === null
            ? 'Nothing billable on this invoice'
            : `Nothing billable; previously ${money(facts.priorTotal, facts.currency)}`,
      };
    case 'NEGATIVE_TOTAL':
      return { title, detail: `Total is ${money(facts.total, facts.currency)}` };
    case 'MISSING_VARIABLE_EXPENSE':
      return {
        title,
        detail:
          facts.lines.length === 1 && facts.lines[0]
            ? `No amount recorded for ${facts.lines[0].description}`
            : `No amount recorded for ${facts.lines.length} expenses`,
      };
    case 'MISSING_EXPECTED_LINES':
      return {
        title,
        detail: facts.missing
          .map((m) => `${m.kind} (was ${money(m.priorAmount, facts.currency)})`)
          .join(', '),
      };
    case 'UNPRICED_ROLE':
      return {
        title,
        detail:
          facts.roles.length === 1 && facts.roles[0]
            ? `${facts.roles[0].lineLabel} — ${facts.roles[0].hours} hours with no agreed rate`
            : `${facts.roles.length} roles worked with no agreed rate`,
      };
    case 'SUMMARY_UNAVAILABLE':
      return {
        title,
        detail:
          facts.roles.length === 1 && facts.roles[0]
            ? `No work description for ${facts.roles[0].lineLabel}`
            : `No work description on ${facts.roles.length} lines`,
      };
    case 'RETAINER_ALREADY_BILLED':
      return {
        title,
        detail: `Retainer billed on ${facts.priorInvoiceNumber} (${money(facts.priorTotal, facts.currency)}); those lines are not repeated here`,
      };
    case 'MANUAL_REVIEW':
      return {
        title,
        detail:
          facts.engagements.length === 1
            ? `${facts.engagements[0]} is set to require manual review`
            : `${facts.engagements.join(', ')} are set to require manual review`,
      };
    case 'ACCOUNTS_INCOMPLETE': {
      const lines = facts.lines.map((l) => l.lineNo).join(', ');
      const which = `Line${facts.lines.length === 1 ? '' : 's'} ${lines} ${
        facts.lines.length === 1 ? 'needs' : 'need'
      } an account`;
      const inactive = facts.inactiveDefaults.map((d) => `${d.code} (${d.slot})`).join(', ');
      return {
        title,
        detail:
          inactive === ''
            ? `${which}. Approval waits until every line is booked.`
            : `${which}: default account ${inactive} is inactive. Pick an active account to approve.`,
      };
    }
    default: {
      // Exhaustiveness guard. Reaching here means a reason was added to the
      // union without a description, which must not compile.
      const unreachable: never = facts;
      return unreachable;
    }
  }
}

/** The loudest severity across a set of flags, or null when there are none.
 *  What the list's severity stripe colours itself from. */
export function peakInvoiceFlagSeverity(
  severities: readonly InvoiceFlagSeverity[],
): InvoiceFlagSeverity | null {
  let peak: InvoiceFlagSeverity | null = null;
  for (const s of severities) {
    if (peak === null || INVOICE_FLAG_SEVERITY_RANK[s] > INVOICE_FLAG_SEVERITY_RANK[peak]) {
      peak = s;
    }
  }
  return peak;
}
